import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  catchError,
  delay,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  withLatestFrom
} from 'rxjs/operators';
import { interval, merge, of, timer } from 'rxjs';
import {
  differenceInMilliseconds,
  addHours,
  startOfHour
} from 'date-fns/esm';
import {
  onWebsocketMessageDispatchUpdateEntities,
  onWebsocketMessageDispatchAddRemoveEntities
} from '@zerops/zef/entities';
import { UserEntity } from '@zerops/zerops/core/user-base';
import {
  TransactionGroupBy,
  TransactionTimeGroupBy,
  TRANSACTION_GROUP_RANGE
} from '@zerops/models/transaction-debit';
import { AppState } from '@zerops/zerops/app';
import { zefRemoveProgress } from '@zerops/zef/progress';
import { zefRemoveError } from '@zerops/zef/errors';
import { zefResetState } from '@zerops/zef/core';
import { TransactionDebitEntity } from './transaction-debit.entity';
import { TransactionDebitBaseApi } from './transaction-debit-base.api';
import {
  periodCost,
  periodCostFail,
  periodCostSuccess,
  trackTransactionDebitGroupItem,
  transactionDebitGroup,
  transactionDebitGroupCancel,
  transactionDebitGroupFail,
  transactionDebitGroupSuccess,
  transactionDebitGroupUpdate
} from './transaction-debit-base.action';
import { FEATURE_NAME } from './transaction-debit-base.constant';
import { selectTrackedItems, selectTransactionDebitBaseGroupedData } from './transaction-debit-base.selector';
import { transactionGroupExists, buildTransactionDebitGroupSearchRequest } from './transaction-debit-base.utils';
import {
  createTimeSeriesGroupFetchEffect,
  createTimeSeriesAutoLoadEffect,
  createTimeSeriesWebSocketUpdateEffect,
  createTimeSeriesTrackingEffect
} from '../shared';

@Injectable()
export class TransactionDebitBaseEffect {

  private _activeClientId$ = this._userEntity.activeClientId$.pipe(filter((d) => !!d));

  private _hourlyTimer$ = timer(
    // the next full hour
    differenceInMilliseconds(startOfHour(addHours(new Date(), 1)), new Date()) + 500,
    // every hour
    60 * 60 * 1000
  );

  // Use shared tracking effect
  private _onTransactionDebitGroupTrackItems$ = createTimeSeriesTrackingEffect({
    groupActionType: transactionDebitGroup,
    trackActionCreator: trackTransactionDebitGroupItem
  })(this._actions$);

  private _setupAddRemoveMessage$ = createEffect(() => this._actions$.pipe(
    onWebsocketMessageDispatchAddRemoveEntities(this._transactionDebitEntity)
  ));

  private _onUpdateStreamMessage$ = createEffect(() => this._actions$.pipe(
    onWebsocketMessageDispatchUpdateEntities(this._transactionDebitEntity)
  ));

  // Use shared group fetch effect
  private _onTransactionDebitGroup$ = createTimeSeriesGroupFetchEffect({
    groupActionType: transactionDebitGroup,
    apiCall: this._api.groupSearch$.bind(this._api),
    existsCheck: transactionGroupExists,
    buildRequest: buildTransactionDebitGroupSearchRequest,
    successActionCreator: transactionDebitGroupSuccess,
    failActionCreator: transactionDebitGroupFail,
    cancelActionCreator: transactionDebitGroupCancel,
    selectGroupedData: selectTransactionDebitBaseGroupedData,
    activeClientId$: this._activeClientId$
  })(this._actions$, this._store);

  private _onTransactionDebitGroupCancel$ = createEffect(() => this._actions$.pipe(
    ofType(transactionDebitGroupCancel),
    delay(0),
    mergeMap(() => [
      zefRemoveError(transactionDebitGroup.type),
      zefRemoveProgress(transactionDebitGroup.type)
    ])
  ));

  // Use shared WebSocket update effect
  private _onDebitGroupUpdate$ = createTimeSeriesWebSocketUpdateEffect({
    featureName: FEATURE_NAME,
    updateActionCreator: transactionDebitGroupUpdate,
    parseMessage: (message, subscriptionName) => {
      const d = message.data;
      const key = subscriptionName.match(/##(.+)##/)?.[1] || '';
      return {
        items: d?.update || [],
        timeGroupBy: d?.timeGroupBy,
        groupBy: d?.groupBy,
        limit: d?.limit,
        key,
        projectId: d?.projectId,
        serviceId: d?.serviceId
      };
    }
  })(this._actions$);

  // Use shared auto-load effect
  private _onTransactionDebitGroupAutoLoad$ = createTimeSeriesAutoLoadEffect({
    activeClientId$: this._activeClientId$,
    defaultGroupRange: TRANSACTION_GROUP_RANGE.last24h,
    groupActionCreator: transactionDebitGroup,
    autoLoadConfigs: [
      {
        groupBy: TransactionGroupBy.Metric,
        groupRange: TRANSACTION_GROUP_RANGE.last24h
      },
      {
        groupBy: TransactionGroupBy.Service,
        groupRange: TRANSACTION_GROUP_RANGE.last24h
      },
      {
        groupBy: TransactionGroupBy.Project,
        groupRange: TRANSACTION_GROUP_RANGE.last24h
      }
    ]
  })();

  // periodically load period cost once clientId becomes available
  private _onClientActiveIdStartPeriodCostLoadTimer$ = createEffect(() => this._activeClientId$.pipe(
    switchMap((clientId) => merge(
      of(undefined),
      interval(60 * 1000).pipe(take(10)),
      this._hourlyTimer$.pipe(
        switchMap(() => interval(60 * 1000).pipe(take(10)))
      )
    ).pipe(
      takeUntil(this._actions$.pipe(ofType(zefResetState))),
      map(() => periodCost(clientId)),
    ))
  ));

  // Hourly update effect with transaction-specific logic
  private _onClientActiveIdStartDebitGroupUpdateTimer$ = createEffect(() => this._activeClientId$.pipe(
    switchMap(() => this._hourlyTimer$.pipe(
      takeUntil(this._actions$.pipe(ofType(zefResetState)))
    )),
    withLatestFrom(this._store.pipe(select(selectTrackedItems))),
    filter(([, trackedItems]: [any, any[]]) => trackedItems.length > 0),
    switchMap(([, trackedItems]: [any, any[]]) => trackedItems
      .filter((item: any) => {
        const timeGroupBy = item.data.groupRange.timeGroupBy;
        return timeGroupBy === TransactionTimeGroupBy.Hours ||
               timeGroupBy === TransactionTimeGroupBy.Days;
      })
      .map((item: any) => {
        const currentRange = item.data.groupRange.getRange();
        return transactionDebitGroup({
          data: {
            ...item.data,
            groupRange: {
              ...item.data.groupRange,
              range: currentRange
            },
            isPeriodUpdate: true
          }
        });
      })
    )
  ));

  private _onPeriodCost$ = createEffect(() => this._actions$.pipe(
    ofType(periodCost),
    switchMap((action) => this._api
      .periodCost$(action.data)
      .pipe(
        map((res) => periodCostSuccess(res, action)),
        catchError((err) => of(periodCostFail(err, action)))
      )
    )
  ))

  constructor(
    private _actions$: Actions,
    private _store: Store<AppState>,
    private _transactionDebitEntity: TransactionDebitEntity,
    private _userEntity: UserEntity,
    private _api: TransactionDebitBaseApi
  ) {}
}
